import { Editor, EditorContent } from '@tiptap/react';
import { ReactNode } from 'react';

import { clstx } from '@payaca/utilities/cvaMerge';

import EditorBubbleMenu from './EditorBubbleMenu';
import EditorToolbar from './EditorToolbar';
import { type EditorToolbarItem } from './editorActions';

interface EditorShellProps {
  editor: Editor | null;
  readOnly: boolean;
  toolbar: EditorToolbarItem[] | false;
  /**
   * Render the editor inline with no surrounding frame (no border/box). The
   * static toolbar is replaced by a bubble menu that surfaces the configured
   * `toolbar` actions on a text selection. Has no effect when `readOnly`.
   */
  borderless?: boolean;
  /**
   * Optional sidebar rendered alongside the editor (e.g. RichTextarea's
   * variable picker). Only shown when not readOnly.
   */
  sidebar?: ReactNode;
}

const EditorShell = ({
  editor,
  readOnly,
  toolbar,
  borderless = false,
  sidebar,
}: EditorShellProps) => {
  const hasSidebar = !!sidebar && !readOnly;
  return (
    <div className={hasSidebar ? 'gap-5 md:flex' : undefined}>
      <div
        className={clstx(
          !readOnly &&
            !borderless &&
            'overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700',
          hasSidebar ? 'md:w-3/4' : 'w-full'
        )}
      >
        {!readOnly && toolbar !== false && !borderless && (
          <EditorToolbar editor={editor} items={toolbar} />
        )}
        <EditorContent editor={editor} />
        {!readOnly && toolbar !== false && borderless && (
          <EditorBubbleMenu editor={editor} items={toolbar} />
        )}
      </div>
      {hasSidebar && sidebar}
    </div>
  );
};

export default EditorShell;
